import { type UUID } from '~/types/common';

import {
  entries,
  filter,
  isPlainObject,
  map,
  pipe,
  unique,
} from '@vestigas/utils';

import { type DeliveryNoteAdmin } from '../types';

const startsWithUuidRegex = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}/i;

/**
 * Extract the UUID from a custom field key.
 * Old customField keys might consist of UUID + label; only get the UUID.
 * @param key - the key to extract the custom field id from
 * @returns the custom field id (UUID)
 */
export const getUuidOfCustomFieldId = (key: string): UUID | undefined => {
  if (typeof key !== 'string') {
    return undefined;
  }

  if (startsWithUuidRegex.test(key)) {
    return key.slice(0, 36);
  }

  return key;
};

/**
 * Extract all custom field ids from a custom data object.
 * @param customData - the custom data object to extract the custom field ids from
 * @returns an array of custom field ids (UUIDs)
 */
const getCustomFieldIdsFromCustomData = (
  customData: Record<string, unknown>,
): UUID[] => {
  if (!isPlainObject(customData)) {
    return [];
  }

  const customFieldIds = pipe(
    customData,
    entries,
    map(([key]) => getUuidOfCustomFieldId(key)),
    filter(Boolean),
  );

  return customFieldIds;
};

/**
 * Extract all custom field ids from the line items of a deliveryNote.
 * @param lineItems - the line items to extract the custom field ids from
 * @returns an array of custom field ids (UUIDs)
 */
const getCustomFieldIdsFromLineItems = (
  lineItems: Array<{ additionalPartyData: Record<string, unknown> }>,
): UUID[] => {
  if (!Array.isArray(lineItems) || !lineItems?.length) {
    return [];
  }

  return lineItems.flatMap(({ additionalPartyData }) =>
    getCustomFieldIdsFromCustomData(additionalPartyData),
  );
};

/**
 * Extract all custom field ids from a deliveryNote - both on the global and on the line item level.
 * @param deliveryNote - the delivery note to extract the custom field ids from
 * @returns an array of custom field ids (UUIDs)
 */
export const getCustomFieldIds = (deliveryNote: DeliveryNoteAdmin): UUID[] => {
  if (!isPlainObject(deliveryNote)) {
    return [];
  }

  const customDataCustomFieldIds = getCustomFieldIdsFromCustomData(
    deliveryNote.assetState?.body?.header?.additionalPartyData,
  );
  const articleCustomFieldIds = getCustomFieldIdsFromLineItems(
    deliveryNote.assetState?.body?.transaction?.logisticsPackage?.[0]?.lineItem,
  );

  return unique([...customDataCustomFieldIds, ...articleCustomFieldIds]).sort();
};
